import { gql, useQuery } from '@apollo/client'
import moment from 'moment'
import { useMemo } from 'react'

import { useGroupFilter } from 'pared/Routes/renderer/groupFilter'
import { getBrandLocationGroupId } from 'pared/utils/brand'

import { useDateFilter } from '../../../dateFilter'
import calc from '../../../utils/calc'
import { useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IDataType {
  trendLocationGroupMetricValues: {
    nodes: {
      businessQuarter: number
      businessMonth: number
      businessWeek: number
      businessWeekOfMonth: number
      metricData: Record<
        string,
        {
          name: string
          unit: string
          value: number
        }
      >
    }[]
  }
}

interface IReturnType {
  period: string
  value: number | null
}

const currentYearQuery = gql`
  query trendLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const priorYearQuery = gql`
  query trendPriorYearLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const twoYearQuery = gql`
  query trendTwoYearLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const threeYearQuery = gql`
  query trendThreeYearLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

const threeYearFullQuery = gql`
  query trendThreeYearFullLocationGroupMetricValues(
    $iStartDate: Date!
    $iEndDate: Date!
    $iGroupBy: String!
    $iFilter: JSON!
  ) {
    trendLocationGroupMetricValues(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iGroupBy: $iGroupBy
      iFilter: $iFilter
    ) {
      nodes {
        locationGroupId
        businessQuarter
        businessMonth
        businessWeek
        businessWeekOfMonth
        metricData
      }
    }
  }
`

export const farwestSssConfigs = {
  period: 'string',
  value: 'percent',
  valueLastYear: 'percent',
  valueTwoYears: 'percent',
  valueTwoYearsStacked: 'percent',
  valueThreeYearsStacked: 'percent',
  valueTwoYearsAgo: 'percent',
} as const

const useFarwestSss = () => {
  const { variables } = useVariables()
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const brandLocationGroupId = getBrandLocationGroupId()
  const yesterday = moment().subtract(1, 'day')
  const adjustedEndDate = moment(endDate, 'YYYY-MM-DD').isAfter(yesterday)
    ? yesterday.format('YYYY-MM-DD')
    : endDate

  const groupBy = variables.farwestTimeinterval?.value[0][0] || 'business_month'

  const dateList = ((groupBy: string) => {
    if (groupBy === 'business_week')
      return variables.date?.getCalendar('week', 'year')?.ids || [[]]
    else if (groupBy === 'business_quarter') return ['Q1', 'Q2', 'Q3', 'Q4']
    else return variables.date?.getCalendar('period', 'year')?.ids || [[]]
  })(groupBy)

  const metrics = variables.selectOptions?.orderMode?.value[0][0]

  const { data: currentData, loading: currentLoading } = useQuery<IDataType>(
    currentYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: adjustedEndDate,
        iGroupBy: groupBy,
        iFilter: {
          location_group_ids: !hasGroupBy
            ? [brandLocationGroupId]
            : groupFilter?.ids,
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [`${metrics}_percentage_diff_to_last_year`],
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
      },
      skip: !startDate || !adjustedEndDate || !variables.date,
    },
  )

  const { data: twoYearData, loading: twoYearLoading } = useQuery<IDataType>(
    twoYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: adjustedEndDate,
        iGroupBy: groupBy,
        iFilter: {
          location_group_ids: !hasGroupBy
            ? [brandLocationGroupId]
            : groupFilter?.ids,
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [`${metrics}_percentage_diff_to_last_year`],
          yoy_offset: 1,
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
      },
      skip: !startDate || !endDate || !variables.date,
    },
  )

  const { data: priorData, loading: priorLoading } = useQuery<IDataType>(
    priorYearQuery,
    {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter: {
          location_group_ids: !hasGroupBy
            ? [brandLocationGroupId]
            : groupFilter?.ids,
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [`${metrics}_percentage_diff_to_last_year`],
          yoy_offset: 1,
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
      },
      skip: !startDate || !endDate || !variables.date,
    },
  )

  const { data: threeYearData, loading: threeYearLoading } =
    useQuery<IDataType>(threeYearQuery, {
      variables: {
        iStartDate: startDate,
        iEndDate: adjustedEndDate,
        iGroupBy: groupBy,
        iFilter: {
          location_group_ids: !hasGroupBy
            ? [brandLocationGroupId]
            : groupFilter?.ids,
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [`${metrics}_percentage_diff_to_last_year`],
          yoy_offset: 2,
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
      },
      skip: !startDate || !adjustedEndDate || !variables.date,
    })

  const { data: threeYearFullData, loading: threeYearFullLoading } =
    useQuery<IDataType>(threeYearFullQuery, {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iGroupBy: groupBy,
        iFilter: {
          location_group_ids: !hasGroupBy
            ? [brandLocationGroupId]
            : groupFilter?.ids,
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [`${metrics}_percentage_diff_to_last_year`],
          yoy_offset: 2,
        },
        intersected_location_group_ids: groupFilter?.intersectedIds,
      },
      skip: !startDate || !endDate || !variables.date,
    })

  const loading =
    currentLoading ||
    priorLoading ||
    twoYearLoading ||
    threeYearLoading ||
    threeYearFullLoading

  return {
    data: useMemo((): IApiDataType => {
      if (
        !currentData?.trendLocationGroupMetricValues.nodes ||
        !priorData?.trendLocationGroupMetricValues.nodes ||
        !twoYearData?.trendLocationGroupMetricValues.nodes ||
        !threeYearData?.trendLocationGroupMetricValues.nodes ||
        !threeYearFullData?.trendLocationGroupMetricValues.nodes ||
        !metrics
      )
        return null

      const metricData = currentData?.trendLocationGroupMetricValues.nodes.map(
        (item): IReturnType => {
          const period = ((groupBy: string) => {
            if (groupBy === 'business_week')
              return `P${item.businessMonth}W${item.businessWeekOfMonth}`
            else if (groupBy === 'business_quarter')
              return `Q${item.businessQuarter}`
            else return `P${item.businessMonth}`
          })(groupBy)

          return {
            period,
            value:
              item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                .value * 100,
          }
        },
      )

      const priorMetricData =
        priorData?.trendLocationGroupMetricValues.nodes.map(
          (item): IReturnType => {
            const period = ((groupBy: string) => {
              if (groupBy === 'business_week')
                return `P${item.businessMonth}W${item.businessWeekOfMonth}`
              else if (groupBy === 'business_quarter')
                return `Q${item.businessQuarter}`
              else return `P${item.businessMonth}`
            })(groupBy)

            return {
              period,
              value:
                item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                  .value * 100,
            }
          },
        )

      const twoYearMetricData =
        twoYearData?.trendLocationGroupMetricValues.nodes.map(
          (item): IReturnType => {
            const period = ((groupBy: string) => {
              if (groupBy === 'business_week')
                return `P${item.businessMonth}W${item.businessWeekOfMonth}`
              else if (groupBy === 'business_quarter')
                return `Q${item.businessQuarter}`
              else return `P${item.businessMonth}`
            })(groupBy)

            return {
              period,
              value:
                item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                  .value * 100,
            }
          },
        )

      const threeYearMetricData =
        threeYearData?.trendLocationGroupMetricValues.nodes.map(
          (item): IReturnType => {
            const period = ((groupBy: string) => {
              if (groupBy === 'business_week')
                return `P${item.businessMonth}W${item.businessWeekOfMonth}`
              else if (groupBy === 'business_quarter')
                return `Q${item.businessQuarter}`
              else return `P${item.businessMonth}`
            })(groupBy)

            return {
              period,
              value:
                item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                  .value * 100,
            }
          },
        )

      const threeYearFullMetricData =
        threeYearFullData?.trendLocationGroupMetricValues.nodes.map(
          (item): IReturnType => {
            const period = ((groupBy: string) => {
              if (groupBy === 'business_week')
                return `P${item.businessMonth}W${item.businessWeekOfMonth}`
              else if (groupBy === 'business_quarter')
                return `Q${item.businessQuarter}`
              else return `P${item.businessMonth}`
            })(groupBy)

            return {
              period,
              value:
                item.metricData?.[`${metrics}_percentage_diff_to_last_year`]
                  .value * 100,
            }
          },
        )

      return dateList
        .map((date) => {
          const currentData = metricData.find((d) => d.period === date)
          const sameDaylyData = priorMetricData.find((d) => d.period === date)
          const twoYearData = twoYearMetricData.find((d) => d.period === date)
          const threeYearData = threeYearMetricData.find(
            (d) => d.period === date,
          )
          const threeYearFullData = threeYearFullMetricData.find(
            (d) => d.period === date,
          )

          return {
            period: date,
            value: currentData?.value,
            valueLastYear: sameDaylyData?.value,
            valueTwoYearsAgo: threeYearFullData?.value,
            valueTwoYearsStacked: calc(
              currentData?.value,
              '+',
              twoYearData?.value,
            ),
            valueThreeYearsStacked: calc(
              calc(currentData?.value, '+', twoYearData?.value),
              '+',
              threeYearData?.value,
            ),
          }
        })
        .filter((d) => d.period)
    }, [currentData, twoYearData, threeYearData, threeYearFullData, priorData]),
    loading,
  }
}

export default useFarwestSss
